/**
 * A field's show/hide condition, mirroring the backend's FieldVisibility so what the admin sees
 * while filling a form matches what will actually be stamped. A field with no condition is always
 * shown; one whose condition does not hold is hidden and never counts as required.
 *
 * Grammar, kept tiny: `otherField=value` (shown when that field currently equals value, which for
 * a checkbox is "true"), or `otherField>=n` (shown when that field parses to at least n). The
 * referenced field name may contain spaces, as the form's own names do, so the operator is found
 * rather than the string simply split.
 */

const parseIntOrZero = (value) => {
  const parsed = parseInt(String(value ?? "").trim(), 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const isFieldVisible = (field, values) => {
  const condition = typeof field?.visibleWhen === "string" ? field.visibleWhen.trim() : "";
  if (!condition) return true;
  const safeValues = values || {};

  const gteAt = condition.indexOf(">=");
  if (gteAt > 0) {
    const controller = condition.slice(0, gteAt).trim();
    const threshold = condition.slice(gteAt + 2).trim();
    return parseIntOrZero(safeValues[controller]) >= parseIntOrZero(threshold);
  }

  const eqAt = condition.indexOf("=");
  if (eqAt > 0) {
    const controller = condition.slice(0, eqAt).trim();
    const expected = condition.slice(eqAt + 1).trim();
    return expected === String(safeValues[controller] ?? "").trim();
  }

  // A malformed condition shows the field rather than silently hiding it, so a mapping typo
  // surfaces as a stray box to fix instead of data quietly dropped.
  return true;
};

/** Only the fields currently shown, in their original order. */
export const visibleFields = (fields, values) =>
  (fields || []).filter((field) => isFieldVisible(field, values));

/**
 * The shown fields grouped by page number, for painting boxes over a page preview. Hiding happens
 * here rather than at paint time so a field on a branch the form did not take neither draws a box
 * nor shows the value it still holds: form 956 carries values for both Part A and Part B, and only
 * the chosen part is stamped onto the rendered PDF.
 */
export const visibleFieldsByPage = (fields, values) => {
  const grouped = new Map();
  for (const field of visibleFields(fields, values)) {
    if (!grouped.has(field.pageNumber)) grouped.set(field.pageNumber, []);
    grouped.get(field.pageNumber).push(field);
  }
  return grouped;
};
